import React, { ReactNode, useMemo, useState } from "react";
import { TableRowSelection } from "antd/es/table/interface";
import { openMessage } from "utils/misc";
import TableCheckbox from "common/Table/TableCheckbox";
import { UseCardsListSelectorProps } from "@shared/common/CardsList/types";
import useCardsListSelector from "@shared/common/CardsList/useCardsListSelector";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import { difference, intersection } from "lodash";
import { getTableRowSelectCheckboxId } from "testIds.ts";

const useTableRowsSelector = <
  TableDataType extends object,
  RowKeyType extends string | number
>({
  selectedRows,
  setSelectedRows,
  getKey,
  allRows = [],
  filteredRows = [],
  getRowSelectable,
  extraData,
  getSelectAllCheckbox,
}: UseCardsListSelectorProps<TableDataType, RowKeyType> & {
  extraData?: any;
  getSelectAllCheckbox?: (
    checked: boolean,
    indeterminate: boolean
  ) => ReactNode;
}) => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const [pagination, setPagination] = useState<{
    pageNumber: number;
    pageSize: number;
  }>();

  const handlePaginationChange = (pageNumber: number, pageSize: number) => {
    setPagination({ pageNumber, pageSize });
  };

  const { isAllRowsSelected, onSelectAll, onSelectRow } = useCardsListSelector({
    selectedRows,
    setSelectedRows,
    getKey,
    allRows,
    filteredRows,
    openMessage,
    getRowSelectable,
  });

  const renderCell = (isSelected: boolean, row: TableDataType) => {
    const errorMessage = getRowSelectable?.(row);
    return (
      <TableCheckbox
        onClick={() => onSelectRow(row, !isSelected)}
        disabled={!!errorMessage}
        checked={isSelected}
        testId={getTableRowSelectCheckboxId(getKey(row))}
      />
    );
  };

  const getColumnTitle = () => {
    const visibleKeys = filteredRows.map(getKey);
    const indeterminate =
      !isAllRowsSelected && !!intersection(selectedRows, visibleKeys).length;
    if (getSelectAllCheckbox) {
      return getSelectAllCheckbox(isAllRowsSelected, indeterminate);
    }
    let onClick = onSelectAll;
    if (isAllRowsSelected) {
      const updatedRows = difference(selectedRows, visibleKeys);
      onClick = () => setSelectedRows?.(updatedRows);
    }
    return (
      <TableCheckbox
        onClick={onClick}
        checked={isAllRowsSelected}
        indeterminate={indeterminate}
      />
    );
  };

  const tableRowProps: TableRowSelection<TableDataType> | undefined =
    useMemo(() => {
      return {
        columnWidth: 50,
        selectedRowKeys: selectedRows,
        onSelect: (row, isSelected) => onSelectRow(row, isSelected),
        renderCell,
        columnTitle: getColumnTitle(),
      };
    }, [
      selectedRows,
      isAllRowsSelected,
      allRows,
      filteredRows,
      extraData,
      user,
    ]);

  return {
    tableRowProps,
    handlePaginationChange,
    pagination,
  };
};

export default useTableRowsSelector;
